import _ChevronDownIcon from "@adsk/alloy-react-icon/es/lib/ChevronDownIcon";
import _styled, { default as _styled2, default as _styled3, default as _styled4 } from "styled-components";
const _excluded = ["style", "className", "children", "expanded", "label", "onExpand", "rightAligned", "title", "animationTimeout", "buttonProps", "containerProps", "onAnimationComplete"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], -1 === t.indexOf(o) && {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (-1 !== e.indexOf(n)) continue; t[n] = r[n]; } return t; }
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import AnimateHeight from '@adsk/alloy-react-animate-height';
import theme, { stylePropType } from '@adsk/alloy-react-theme';
const getChevronAnimation = _ref => {
  let {
    expanded
  } = _ref;
  const transform = expanded ? 'rotate(0deg)' : 'rotate(-90deg)';
  return {
    transform,
    transition: 'transform 0.15s linear'
  };
};
/**
 * An expandable panel component which can be used in an accordion.
 */
const AccordionItem = /*#__PURE__*/React.forwardRef((_ref2, ref) => {
  let {
      style,
      className,
      children,
      expanded = false,
      label,
      onExpand,
      rightAligned = false,
      title,
      animationTimeout,
      buttonProps,
      containerProps,
      onAnimationComplete
    } = _ref2,
    props = _objectWithoutProperties(_ref2, _excluded);
  const chevronAnimation = useMemo(() => getChevronAnimation({
    expanded
  }), [expanded]);
  return /*#__PURE__*/React.createElement(_StyledDiv, _extends({
    className: className,
    ref: ref,
    role: "heading",
    "aria-level": 1
  }, props, {
    $_css: [{
      margin: `${theme.spacing.XXXS}px 0`,
      width: '100%',
      ':focus': {
        boxShadow: theme.halos.focus
      }
    }, style]
  }), /*#__PURE__*/React.createElement(_StyledButton, _extends({
    className: className,
    "aria-expanded": expanded,
    onClick: onExpand
  }, buttonProps, {
    $_css2: [_objectSpread(_objectSpread(_objectSpread(_objectSpread({
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      backgroundColor: theme.tokens.colors.background.secondary.value,
      color: theme.tokens.colors.text.secondary.value,
      cursor: 'pointer',
      textAlign: 'left',
      outline: 'none',
      border: 'none'
    }, rightAligned && {
      flexDirection: 'row-reverse'
    }), theme.typography.labelSmall), {}, {
      minHeight: '32px',
      position: 'relative'
    }, expanded && {
      color: theme.tokens.colors.text.primary.value
    }), {}, {
      '&:hover, &:focus': {
        color: theme.tokens.colors.text.primary.value
      }
    }), style]
  }), /*#__PURE__*/React.createElement(_ChevronDownIcon, {
    size: 16,
    style: _objectSpread({
      marginLeft: theme.spacing.XS,
      marginRight: rightAligned ? theme.spacing.M : 0
    }, chevronAnimation)
  }), /*#__PURE__*/React.createElement(_StyledDiv2, {
    $_css3: [{
      flex: 'auto',
      marginBottom: theme.spacing.XXS,
      marginLeft: rightAligned ? theme.spacing.L : theme.spacing.XXS,
      marginRight: theme.spacing.M,
      marginTop: theme.spacing.XXS
    }]
  }, title), !rightAligned && /*#__PURE__*/React.createElement(_StyledDiv3, {
    $_css4: [{
      color: theme.tokens.colors.text.secondary.value,
      flex: '0 0 auto',
      marginRight: theme.spacing.M
    }]
  }, label)), /*#__PURE__*/React.createElement(AnimateHeight, _extends({
    isOpen: !!expanded,
    timeout: animationTimeout,
    onAnimationComplete: onAnimationComplete
  }, containerProps), children));
});
AccordionItem.displayName = 'AccordionItem';
AccordionItem.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Items to be displayed when the panel is expanded */
  children: PropTypes.any,
  /** Whether or not the panel is expanded */
  expanded: PropTypes.bool,
  /** The secondary label to display on the right of the title bar */
  label: PropTypes.any,
  /** Callback method for when the panel is expanded or contracted */
  onExpand: PropTypes.func,
  /** Whether or not the chevron icon is right-aligned. If true, label will be
   *  hidden */
  rightAligned: PropTypes.bool,
  /** The main title to display on the left of the title bar */
  title: PropTypes.any,
  /** Height animation timeout */
  animationTimeout: PropTypes.number,
  /** Callback when animation is complete */
  onAnimationComplete: PropTypes.func
};
export default AccordionItem;
var _StyledDiv = _styled("div")`${p => p.$_css}`;
var _StyledButton = _styled2("button")`${p => p.$_css2}`;
var _StyledDiv2 = _styled3("div")`${p => p.$_css3}`;
var _StyledDiv3 = _styled4("div").withConfig({
  displayName: "AccordionItem___StyledDiv3",
  componentId: "sc-1buxpa4-0"
})(["", ""], p => p.$_css4);
//# sourceMappingURL=AccordionItem.js.map